import { useState, useEffect } from "react";
import { getTestList } from "../utils/api-communication/RestApiCli";

interface UseTestListOptions {
  /**
   * When `true`, the hook does not fetch and immediately resolves to an empty
   * list. Useful for callers that conditionally need a result by UUID — passing
   * an empty filter object would otherwise trigger a full-list fetch.
   *
   * Note: passing an empty `filterParams` object also skips the fetch, so this
   * flag is most useful when the caller wants to keep a non-empty filter but
   * still opt out for a render.
   */
  skip?: boolean;
}

const isEmptyFilter = (params: object) => Object.keys(params).length === 0;

const useTestList = (initialFilterParams = {}, options: UseTestListOptions = {}) => {
  const [testList, setTestList] = useState([]);
  const [testListLoading, setTestListLoading] = useState(
    !options.skip && !isEmptyFilter(initialFilterParams),
  );
  const [testListError, setTestListError] = useState(null);
  const [filterParams, setFilterParams] = useState(initialFilterParams);

  const { skip = false } = options;

  useEffect(() => {
    if (skip || isEmptyFilter(filterParams)) {
      setTestList([]);
      setTestListLoading(false);
      setTestListError(null);
      return;
    }

    const fetchTestList = () => {
      setTestListLoading(true);
      getTestList(
        filterParams,
        (response) => {
          setTestList(response.data);
          setTestListLoading(false);
        },
        (err) => {
          setTestListError(err);
          setTestListLoading(false);
        },
      );
    };

    fetchTestList();
  }, [filterParams, skip]);

  return { testList, testListLoading, filterParams, setFilterParams, testListError };
};

export default useTestList;
